import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2, ChefHat, Timer, MapPin, Utensils, ShoppingBag, Bike, Coffee, AlertTriangle, Volume2, VolumeX, CalendarClock } from "lucide-react";
import { KDS_SOUND_PRESETS, playKdsSound } from "../lib/kdsSound";
import { promiseLine } from "../lib/retail/fulfillmentPresentation";
import TakeoutBadge from "../components/retail/TakeoutBadge";

// Commerce Cleanup + KDS Sprint (Feb 2026) — Kitchen Display System.
// Tablet-optimised (Samsung Galaxy Tab S8 / 10"–11" iPads). Polls the
// existing admin orders endpoint every 6 seconds and surfaces only
// PAID orders that are still in flight. Status transitions go through
// the new `/orders/:id/state` route which drives the canonical state
// machine; the legacy `status` column is kept in sync server-side so
// the Retail Orders admin and customer-facing Order Status page see
// the same updates without code changes.

const POLL_MS = 6000;

// KDS notification sound — preference is per-device (localStorage). The
// AudioContext requires a user gesture to start in modern browsers, so
// the initial poll cycle is suppressed; only orders arriving AFTER the
// first sync trigger the sound.
const SOUND_STORAGE_KEY = "rudras.kds.sound";
const DEFAULT_SOUND = "chime";

function readStoredSound() {
  if (typeof window === "undefined") return DEFAULT_SOUND;
  try {
    const v = window.localStorage.getItem(SOUND_STORAGE_KEY);
    return v && KDS_SOUND_PRESETS.some((p) => p.id === v) ? v : DEFAULT_SOUND;
  } catch (_e) {
    return DEFAULT_SOUND;
  }
}

const COLUMN_CONFIG = [
  {
    key: "new",
    label: "New",
    description: "Tap to start preparing",
    accent: "bg-amber-500",
    tint: "bg-amber-50/40 border-amber-200/70",
    badge: "bg-amber-500 text-white",
    matches: (s) => s === "created" || s === "confirmed" || !s,
    nextLabel: "Start Preparing",
    nextState: "preparing",
  },
  {
    key: "preparing",
    label: "Preparing",
    description: "On the line",
    accent: "bg-sky-500",
    tint: "bg-sky-50/40 border-sky-200/70",
    badge: "bg-sky-500 text-white",
    matches: (s) => s === "preparing",
    nextLabel: "Mark Ready",
    nextState: "ready",
  },
  {
    key: "ready",
    label: "Ready",
    description: "Awaiting handoff",
    accent: "bg-emerald-500",
    tint: "bg-emerald-50/50 border-emerald-200/70",
    badge: "bg-emerald-500 text-white",
    matches: (s) => s === "ready",
    nextLabel: "Complete",
    nextState: "completed",
  },
];

const MODE_ICON = {
  dine_in: Utensils,
  pickup: ShoppingBag,
  takeout: ShoppingBag,
  delivery: Bike,
};

const MODE_LABEL = {
  dine_in: "Dine-In",
  pickup: "Pickup",
  takeout: "Takeout",
  delivery: "Delivery",
};

// KDS item-level preparation (Sep 2026, restored Oct 2026) — see
// `toggleItemPrepared` below.
const KDS_COMPACT_ITEM_LIMIT = 8;

const prepKey = (orderId, itemId) => `${orderId}:${itemId}`;

// A poll that was already in flight when a tick was saved carries the
// PRE-tick server state. Laying it straight onto the board would visibly
// un-tick the line for one poll cycle. So a tick stays "pending" until a
// poll that STARTED after its save settled has come back — that poll is
// server truth (including another tablet's later change) and retires it.
function overlayPendingPrep(orders, pending, pollEpoch) {
  for (const [key, p] of pending) {
    if (p.settledAt !== null && pollEpoch > p.settledAt) pending.delete(key);
  }
  if (pending.size === 0) return orders;
  return orders.map((o) => {
    const items = o.items || [];
    if (!items.some((it) => it.item_id && pending.has(prepKey(o.id, it.item_id)))) return o;
    return {
      ...o,
      items: items.map((it) => {
        const p = it.item_id ? pending.get(prepKey(o.id, it.item_id)) : null;
        return p ? { ...it, prepared: p.completed } : it;
      }),
    };
  });
}

function prepErrorMessage(e) {
  const detail = e?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.message) return detail.message;
  if (!e?.response) return "Couldn't save that tick — check the connection and try again.";
  if (e.response.status === 404) return "That item is no longer on this order. The board has been refreshed.";
  return "Couldn't save that tick. Please try again.";
}

function elapsedLabel(iso) {
  if (!iso) return "—";
  const placedAt = new Date(iso).getTime();
  if (Number.isNaN(placedAt)) return "—";
  const mins = Math.max(0, Math.floor((Date.now() - placedAt) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const rest = mins % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

function elapsedTone(iso) {
  if (!iso) return "text-stone-500";
  const placedAt = new Date(iso).getTime();
  if (Number.isNaN(placedAt)) return "text-stone-500";
  const mins = Math.floor((Date.now() - placedAt) / 60000);
  if (mins >= 20) return "text-red-600 font-semibold";
  if (mins >= 10) return "text-amber-600 font-semibold";
  return "text-stone-500";
}

export default function KitchenDisplayPage() {
  const { slug } = useParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [now, setNow] = useState(Date.now());
  const newIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);
  const [newFlash, setNewFlash] = useState(new Set());
  const [sound, setSound] = useState(readStoredSound);
  // Item preparation: `prepPendingRef` maps `orderId:itemId` to
  // `{ completed, settledAt }` (settledAt = the poll epoch at which the
  // save returned, or null while in flight); `prepBusy` mirrors the
  // in-flight keys so their checkboxes render disabled.
  const prepPendingRef = useRef(new Map());
  const pollEpochRef = useRef(0);
  const [prepBusy, setPrepBusy] = useState(() => new Set());
  const soundRef = useRef(sound);
  useEffect(() => {
    soundRef.current = sound;
    try { window.localStorage.setItem(SOUND_STORAGE_KEY, sound); } catch (_e) { /* ignore */ }
  }, [sound]);

  const load = useCallback(async (opts = {}) => {
    const silent = !!opts.silent;
    if (!silent) setLoading(true);
    const pollEpoch = ++pollEpochRef.current;
    try {
      const { data } = await api.get("/retail/orders", {
        // Server-side filter: kitchen ONLY sees paid orders that
        // haven't reached a terminal state yet.
        params: { payment_status: "paid", active_only: true, limit: 200 },
      });
      const fresh = (data?.orders || []).filter((o) => {
        const s = (o.state || "created").toLowerCase();
        return s !== "completed" && s !== "cancelled" && s !== "refunded";
      });
      // Detect newly-arrived orders so we can pulse the card and ring
      // the configured notification sound. Skip the FIRST load — we
      // don't want to chime for the baseline backlog the operator just
      // walked into.
      const flashSeed = new Set();
      const knownIds = newIdsRef.current;
      if (!firstLoadRef.current) {
        for (const o of fresh) {
          if (!knownIds.has(o.id)) flashSeed.add(o.id);
        }
      }
      newIdsRef.current = new Set(fresh.map((o) => o.id));
      firstLoadRef.current = false;
      if (flashSeed.size > 0) {
        setNewFlash(flashSeed);
        setTimeout(() => setNewFlash(new Set()), 4000);
        try { playKdsSound(soundRef.current); } catch (_e) { /* ignore */ }
      }
      setOrders(overlayPendingPrep(fresh, prepPendingRef.current, pollEpoch));
      setError(null);
      setLastSync(new Date());
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        setError("Session expired. Please sign in again from the dashboard.");
      } else if (status === 403) {
        setError("Retail mode is not enabled for this account.");
      } else {
        setError("Could not load orders. Will retry in a moment.");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const id = setInterval(() => load({ silent: true }), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Lightweight clock so card timers refresh without a network call.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, []);

  // Sep 2026 (restored Oct 2026) — item-level preparation.
  //
  // Persisted server-side (`PATCH /retail/orders/:id/items/:itemId/
  // preparation`) rather than kept in this tab, because a kitchen runs on
  // more than one screen: a refresh, a second tablet, and the next person
  // to walk up must all see the same ticks. The local update is an
  // optimistic echo so the checkbox responds instantly on a touchscreen;
  // `overlayPendingPrep` keeps a poll that was already in flight from
  // reverting it, and a failure rolls the echo back.
  //
  // One write per line at a time: while a line's save is in flight its
  // checkbox is disabled, so a fast check-then-uncheck can never reach
  // the server out of order.
  //
  // Deliberately does NOT advance the order. Checking the last line does
  // not move the ticket to Ready — the explicit order-level "Mark Ready"
  // button remains the only thing that changes order state.
  const toggleItemPrepared = useCallback(async (order, item, nextCompleted) => {
    if (!item?.item_id) return;
    const key = prepKey(order.id, item.item_id);
    const pending = prepPendingRef.current;
    if (pending.get(key)?.settledAt === null) return;
    const previous = Boolean(item.prepared);
    const apply = (completed) => setOrders((prev) => prev.map((o) => (
      o.id !== order.id ? o : {
        ...o,
        items: (o.items || []).map((it) => (
          it.item_id === item.item_id ? { ...it, prepared: completed } : it
        )),
      }
    )));
    const setBusy = (on) => setPrepBusy((prev) => {
      const next = new Set(prev);
      if (on) next.add(key); else next.delete(key);
      return next;
    });

    pending.set(key, { completed: nextCompleted, settledAt: null });
    setBusy(true);
    apply(nextCompleted);
    try {
      await api.patch(
        `/retail/orders/${order.id}/items/${encodeURIComponent(item.item_id)}/preparation`,
        { completed: nextCompleted },
      );
      pending.set(key, { completed: nextCompleted, settledAt: pollEpochRef.current });
    } catch (e) {
      pending.delete(key);
      apply(previous);
      toast.error(prepErrorMessage(e));
      // 404/409 means the board is out of date (order moved to Ready on
      // another screen, line voided) — show the truth now.
      const status = e?.response?.status;
      if (status === 404 || status === 409) load({ silent: true });
    } finally {
      setBusy(false);
    }
  }, [load]);

  const advance = async (order, target) => {
    if (updatingId) return;
    setUpdatingId(order.id);
    try {
      await api.patch(`/retail/orders/${order.id}/state`, { state: target });
      toast.success(`${order.order_code} → ${target}`);
      await load({ silent: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object" && detail.message)
        || (typeof detail === "string" && detail)
        || "Could not update order state";
      toast.error(msg);
    } finally {
      setUpdatingId(null);
    }
  };

  const grouped = useMemo(() => {
    const out = { new: [], preparing: [], ready: [] };
    for (const o of orders) {
      const s = (o.state || "created").toLowerCase();
      const col = COLUMN_CONFIG.find((c) => c.matches(s));
      if (col) out[col.key].push(o);
    }
    return out;
  }, [orders]);

  return (
    <div
      className="min-h-screen bg-stone-950 text-stone-100 flex flex-col"
      data-testid="kitchen-display-page"
    >
      <Header
        slug={slug}
        counts={grouped}
        lastSync={lastSync}
        loading={loading}
        onRefresh={() => load()}
        sound={sound}
        onSoundChange={setSound}
      />

      {error && (
        <div
          className="mx-6 mt-4 rounded-lg border border-red-700/50 bg-red-900/30 text-red-200 px-4 py-3 text-sm flex items-center gap-2"
          data-testid="kitchen-error"
        >
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      <ScheduledQueue onActivated={() => load({ silent: true })} />

      {loading ? (
        <div className="flex-1 grid place-items-center text-stone-400" data-testid="kitchen-loading">
          <div className="flex items-center gap-2 text-base">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading kitchen orders…
          </div>
        </div>
      ) : orders.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 px-4 md:px-6 py-4 md:py-6 overflow-hidden">
          {COLUMN_CONFIG.map((col) => (
            <Column
              key={col.key}
              col={col}
              orders={grouped[col.key]}
              advance={advance}
              onToggleItem={toggleItemPrepared}
              prepBusy={prepBusy}
              updatingId={updatingId}
              newFlash={newFlash}
              now={now}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Catalog Fulfillment Scheduling Phase 2 (Sep 2026) — forward visibility.
//
// Paid FUTURE orders, deliberately rendered as a separate surface rather
// than mixed into the active columns. They are not work yet: the backend's
// `active_only` filter already excludes them (server-side, because
// printing, the admin list and the watchdog read the same orders), so this
// panel is the only place the kitchen can see what is coming.
//
// The Activate control is an operational fallback for when a customer is
// early or the kitchen is quiet — it calls the same atomic activation the
// worker calls, so clicking it while the worker is mid-flight is a benign
// no-op rather than a second activation.
function ScheduledQueue({ onActivated }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activatingId, setActivatingId] = useState(null);
  const [open, setOpen] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/retail/scheduled-orders", { params: { limit: 100 } });
      setRows(data?.orders || []);
    } catch (_e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const activate = async (row) => {
    if (activatingId) return;
    setActivatingId(row.id);
    try {
      const { data } = await api.post(`/retail/scheduled-orders/${row.id}/activate`);
      if (data?.status === "activated") {
        toast.success(`${row.order_code} sent to the kitchen`);
      } else {
        // The worker got there first — expected, and not an error.
        toast.success(`${row.order_code} is already on the line`);
      }
      await load();
      onActivated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not activate that order");
    } finally {
      setActivatingId(null);
    }
  };

  if (loading || rows.length === 0) return null;

  return (
    <section
      className="mx-4 md:mx-6 mb-2 rounded-lg border border-stone-800 bg-stone-900/60"
      data-testid="kitchen-scheduled-queue"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left"
        data-testid="kitchen-scheduled-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-stone-200">
          <CalendarClock className="w-4 h-4 text-sky-400" />
          Scheduled — not started
          <span
            className="ml-1 rounded-full bg-sky-500/20 text-sky-300 px-2 py-0.5 text-xs"
            data-testid="kitchen-scheduled-count"
          >
            {rows.length}
          </span>
        </span>
        <span className="text-xs text-stone-500">{open ? "Hide" : "Show"}</span>
      </button>

      {open && (
        <div className="px-4 pb-3 space-y-1.5">
          {rows.map((row) => (
            <div
              key={row.id}
              className="flex items-center justify-between gap-3 rounded-md bg-stone-950/60 border border-stone-800 px-3 py-2"
              data-testid={`kitchen-scheduled-row-${row.id}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-semibold">{row.order_code}</span>
                  <span className="text-stone-400 truncate">{row.customer_name}</span>
                  <span className="text-xs uppercase tracking-wide text-stone-500">{row.mode}</span>
                </div>
                {/* Sep 2026 — the line must never have to infer a
                    scheduled time from an order's age. Two facts, stated
                    plainly: when the customer was promised it, and when
                    the kitchen should start. */}
                <div className="text-xs text-sky-300 font-medium" data-testid={`kitchen-scheduled-promise-${row.id}`}>
                  {promiseLine(row) || row.promise_label}
                </div>
                {row.activate_at && (
                  <div className="text-xs text-stone-400">
                    Kitchen starts around{" "}
                    {new Date(row.activate_at).toLocaleTimeString([], {
                      hour: "numeric", minute: "2-digit",
                      ...(row.timezone ? { timeZone: row.timezone } : {}),
                    })}
                  </div>
                )}
                {(row.items || []).length > 0 && (
                  <div className="text-xs text-stone-400 truncate" data-testid={`kitchen-scheduled-items-${row.id}`}>
                    {row.items.map((it) => `${it.quantity}× ${it.product_name}`).join(", ")}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {row.schedule_status === "failed" && (
                  <span className="text-xs text-red-300" data-testid={`kitchen-scheduled-failed-${row.id}`}>
                    needs support
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => activate(row)}
                  disabled={activatingId === row.id}
                  data-testid={`kitchen-scheduled-activate-${row.id}`}
                  className="rounded-md border border-stone-700 hover:border-stone-500 px-2.5 py-1 text-xs text-stone-200 disabled:opacity-50"
                >
                  {activatingId === row.id ? "Starting…" : "Start now"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


function Header({ counts, lastSync, loading, onRefresh, slug, sound, onSoundChange }) {
  const total = (counts.new?.length || 0) + (counts.preparing?.length || 0) + (counts.ready?.length || 0);
  const muted = sound === "off";
  return (
    <header
      className="px-6 py-4 border-b border-stone-800 flex items-center justify-between flex-wrap gap-3"
      data-testid="kitchen-header"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-orange-500/20 text-orange-400 grid place-items-center">
          <ChefHat className="w-6 h-6" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-stone-400">Kitchen Display</div>
          <h1 className="text-xl font-semibold tracking-tight" data-testid="kitchen-tenant-slug">{slug}</h1>
        </div>
      </div>
      <div className="flex items-center gap-3 text-sm text-stone-300">
        <span className="px-3 py-1.5 rounded-full bg-stone-800/70 border border-stone-700" data-testid="kitchen-active-count">
          {total} active
        </span>
        <span className="hidden sm:inline text-xs text-stone-500" data-testid="kitchen-last-sync">
          {lastSync ? `Synced ${lastSync.toLocaleTimeString()}` : "Syncing…"}
        </span>
        <SoundControl value={sound} onChange={onSoundChange} muted={muted} />
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          data-testid="kitchen-refresh"
          className="text-xs px-3 py-1.5 rounded-md border border-stone-700 hover:border-stone-500 hover:bg-stone-800 transition disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>
    </header>
  );
}

function SoundControl({ value, onChange, muted }) {
  // Two pieces in one — a quick mute toggle (icon button) plus a
  // dropdown for the preset selection. Both write through the same
  // setter so the parent's localStorage effect persists them.
  const Icon = muted ? VolumeX : Volume2;
  const handleToggle = () => {
    onChange(muted ? DEFAULT_SOUND : "off");
    if (muted) {
      // User just turned sound back on — give them an audible confirmation
      // and unlock the AudioContext via this gesture.
      try { playKdsSound(DEFAULT_SOUND); } catch (_e) { /* ignore */ }
    }
  };
  const handleChange = (e) => {
    const next = e.target.value;
    onChange(next);
    if (next !== "off") {
      // Preview the chosen preset so the operator can compare them.
      try { playKdsSound(next); } catch (_e) { /* ignore */ }
    }
  };
  return (
    <div className="inline-flex items-center gap-1.5" data-testid="kitchen-sound-control">
      <button
        type="button"
        onClick={handleToggle}
        title={muted ? "Sound is off — tap to enable" : "Tap to mute"}
        aria-label={muted ? "Enable notification sound" : "Mute notification sound"}
        data-testid="kitchen-sound-toggle"
        className={`p-1.5 rounded-md border transition ${muted ? "border-stone-700 text-stone-500 hover:text-stone-300" : "border-orange-500/60 text-orange-300 hover:bg-orange-500/10"}`}
      >
        <Icon className="w-4 h-4" />
      </button>
      <select
        value={value}
        onChange={handleChange}
        data-testid="kitchen-sound-select"
        className="bg-stone-900 border border-stone-700 text-xs rounded-md px-2 py-1.5 text-stone-300 focus:outline-none focus:ring-1 focus:ring-orange-400/50"
      >
        {KDS_SOUND_PRESETS.map((p) => (
          <option key={p.id} value={p.id}>{p.label}</option>
        ))}
      </select>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="flex-1 grid place-items-center text-stone-400 text-center px-6"
      data-testid="kitchen-empty"
    >
      <div className="max-w-md">
        <div className="w-16 h-16 rounded-full bg-stone-800 grid place-items-center mx-auto mb-4">
          <Coffee className="w-7 h-7 text-stone-500" />
        </div>
        <div className="text-lg font-semibold text-stone-200">All caught up</div>
        <div className="text-sm mt-1">No paid orders waiting. New tickets appear here within a few seconds.</div>
      </div>
    </div>
  );
}

function Column({ col, orders, advance, onToggleItem, prepBusy, updatingId, newFlash, now }) {
  return (
    <section
      className={`rounded-2xl border ${col.tint} bg-stone-900/40 backdrop-blur flex flex-col overflow-hidden`}
      data-testid={`kitchen-column-${col.key}`}
    >
      <header className="flex items-center gap-3 px-4 py-3 border-b border-stone-800/60">
        <span className={`inline-block w-2 h-6 rounded-full ${col.accent}`} aria-hidden />
        <div className="flex-1">
          <div className="text-sm uppercase tracking-wider font-semibold text-stone-100">{col.label}</div>
          <div className="text-xs text-stone-400">{col.description}</div>
        </div>
        <span
          className={`text-xs font-bold px-2.5 py-1 rounded-full ${col.badge}`}
          data-testid={`kitchen-column-count-${col.key}`}
        >
          {orders.length}
        </span>
      </header>
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {orders.length === 0 ? (
          <div className="text-center text-xs text-stone-500 py-10 border border-dashed border-stone-700 rounded-xl">
            Nothing here yet
          </div>
        ) : (
          orders.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              col={col}
              flashing={newFlash.has(o.id)}
              onAdvance={() => advance(o, col.nextState)}
              onToggleItem={onToggleItem}
              prepBusy={prepBusy}
              busy={updatingId === o.id}
              nowTick={now}
            />
          ))
        )}
      </div>
    </section>
  );
}

function OrderCard({ order, col, flashing, onAdvance, onToggleItem, prepBusy, busy, nowTick }) {
  // Sep 2026 (restored Oct 2026) — the compact card still shows eight
  // lines. That cap is what keeps three columns of tickets glanceable on
  // a 10" tablet, so it stays the default; what changes is that the rest
  // are REACHABLE rather than summarised away, because a line a cook
  // cannot see is a line they cannot tick. Expansion is per-card and
  // local; preparation state itself is server-persisted.
  const [expanded, setExpanded] = useState(false);
  const items = order.items || [];
  const overflow = Math.max(0, items.length - KDS_COMPACT_ITEM_LIMIT);
  const visibleItems = expanded ? items : items.slice(0, KDS_COMPACT_ITEM_LIMIT);
  const ModeIcon = MODE_ICON[order.fulfillment_mode] || Utensils;
  const modeLabel = MODE_LABEL[order.fulfillment_mode] || order.fulfillment_mode || "Order";
  const itemTotal = (order.items || []).reduce((n, it) => n + (it.quantity || 0), 0);
  return (
    <article
      data-testid={`kitchen-order-${order.id}`}
      data-order-code={order.order_code}
      className={`rounded-xl bg-white text-stone-900 shadow-lg border ${flashing ? "border-orange-400 ring-2 ring-orange-300/60 animate-pulse" : "border-stone-200"} overflow-hidden`}
    >
      <div className="px-4 pt-3 pb-2 flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-stone-500">
            <ModeIcon className="w-3 h-3" /> {modeLabel}
            {order.table_number && (
              <span className="inline-flex items-center gap-0.5 text-stone-700">
                <MapPin className="w-3 h-3" /> T{order.table_number}
              </span>
            )}
          </div>
          <div className="font-mono text-lg font-bold mt-0.5 leading-tight">{order.order_code}</div>
        </div>
        <div className={`inline-flex items-center gap-1 text-xs ${elapsedTone(order.placed_at)}`} title={order.placed_at || ""}>
          <Timer className="w-3.5 h-3.5" />
          <span data-testid={`kitchen-elapsed-${order.id}`}>{elapsedLabel(order.placed_at)}</span>
        </div>
      </div>

      <div className="px-4 pb-3 space-y-1 text-sm">
        {visibleItems.map((it, idx) => {
          // Ready has left the line: its item state is history to read,
          // not to edit. Checking is a NEW/PREPARING gesture. (Completed,
          // cancelled and refunded orders never reach the board at all.)
          const readOnly = col.key === "ready";
          // A legacy line with no `item_id` has no stable address to
          // store state against. It renders normally but without a
          // checkbox — keying off the array index instead would let one
          // tick land on a different dish the moment the order is
          // re-read.
          const checkable = Boolean(it.item_id) && !readOnly;
          const checked = Boolean(it.prepared);
          const saving = checkable && prepBusy?.has(prepKey(order.id, it.item_id));
          const lineId = `kds-item-${order.id}-${it.item_id || idx}`;
          return (
            <div
              key={it.item_id || idx}
              className="flex items-start gap-2 leading-tight"
              data-testid={`kitchen-order-${order.id}-item-${idx}`}
              data-item-id={it.item_id || ""}
              data-prepared={checked ? "true" : "false"}
            >
              {/* The whole row is the target, not just the glyph: a 24px
                  box inside a 44px-tall row whose label is bound to it,
                  so anywhere on the line registers. That is what a cook's
                  thumb needs on the Galaxy Tab S8 this screen runs on,
                  and the row sits inside the card's padded item area,
                  clear of the full-width action button along the bottom
                  edge, so neither is hit by accident. */}
              {checkable ? (
                <input
                  type="checkbox"
                  id={lineId}
                  checked={checked}
                  disabled={saving}
                  onChange={(e) => onToggleItem?.(order, it, e.target.checked)}
                  data-testid={`kitchen-item-checkbox-${order.id}-${idx}`}
                  aria-label={`Mark ${it.quantity} × ${it.product_name} as prepared`}
                  className="mt-2.5 h-6 w-6 shrink-0 cursor-pointer accent-emerald-600 disabled:opacity-60 disabled:cursor-wait focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                />
              ) : (
                <span
                  className={`mt-2.5 h-6 w-6 shrink-0 rounded border grid place-items-center text-xs font-bold ${checked ? "border-emerald-600 bg-emerald-600 text-white" : "border-stone-300"}`}
                  aria-hidden="true"
                  data-testid={`kitchen-item-readonly-${order.id}-${idx}`}
                >
                  {checked ? "✓" : ""}
                </span>
              )}
              <label
                htmlFor={checkable ? lineId : undefined}
                className={`flex-1 min-h-[44px] flex flex-col justify-center py-1.5 ${checkable ? "cursor-pointer" : ""}`}
              >
                <span>
                  {/* A completed line is dimmed and struck through but
                      stays READABLE — stone-500 keeps it above the 4.5:1
                      body-text ratio, where the stone-400 that reads as
                      "obviously done" would not. A cook still has to be
                      able to check what was made. */}
                  <span className={checked ? "text-stone-500 line-through" : ""}>
                    <span className="font-bold text-stone-900">{it.quantity}×</span>{" "}
                    <span className="text-stone-800">{it.product_name}</span>
                  </span>
                  {/* Customer-selected spice (Sep 2026, restored). The
                      kitchen cooks the CUSTOMER's choice, not the catalog
                      default, so this reads the line's own value. Text,
                      never colour alone. Informational: no state, no
                      transition, no lifecycle change. */}
                  {it.spice_level_label && (
                    <span
                      className="ml-1.5 align-middle inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border bg-rose-100 text-rose-800 border-rose-300"
                      data-testid={`kitchen-item-spice-${order.id}-${idx}`}
                      title={`Spice level: ${it.spice_level_label}`}
                    >
                      {it.spice_level_label}
                    </span>
                  )}
                  {/* Item-level Takeout marker (Sep 2026) — the kitchen
                      has to know which dishes to box even though the
                      ORDER is Dine-In. Text, never colour alone, so it
                      survives a bright pass line and a screen reader
                      alike. It changes no state, no transition and no
                      lifecycle behaviour. */}
                  {it.item_takeout && (
                    <span className="ml-1.5 align-middle">
                      <TakeoutBadge testId={`kitchen-item-takeout-${order.id}-${idx}`} />
                    </span>
                  )}
                </span>
                {it.note && (
                  <div className={`text-xs mt-0.5 ${checked ? "text-stone-500 line-through" : "text-amber-700"}`}>
                    {it.note}
                  </div>
                )}
              </label>
            </div>
          );
        })}
        {overflow > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`kitchen-items-toggle-${order.id}`}
            aria-expanded={expanded}
            className="w-full min-h-[44px] text-xs font-semibold text-stone-600 hover:text-stone-900 hover:bg-stone-50 rounded-md border border-dashed border-stone-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400"
          >
            {expanded ? "Show fewer" : `Show all items (+${overflow} more)`}
          </button>
        )}
        {items.length === 0 && (
          <div className="text-xs text-stone-400 italic">No items recorded</div>
        )}
      </div>

      <div className="px-4 py-2 bg-stone-50 border-t border-stone-100 flex items-center justify-between text-[11px] text-stone-500">
        <span data-testid={`kitchen-customer-${order.id}`}>{order.customer?.name || "Guest"}</span>
        <span>{itemTotal} items</span>
      </div>

      <button
        type="button"
        onClick={onAdvance}
        disabled={busy}
        data-testid={`kitchen-advance-${order.id}`}
        className={`w-full h-12 text-white font-semibold text-sm tracking-wide ${col.accent} hover:brightness-110 transition active:scale-[0.99] disabled:opacity-60`}
      >
        {busy ? "Updating…" : col.nextLabel} →
      </button>
    </article>
  );
}
